import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import eventService from '../../services/eventService';
import { userService } from '../../services/userService';
import { useFeedback } from '../../contexts/FeedbackContext';
import { saveErrorMessage } from '../../utils/saveErrors';
import { useResponsive } from '../../hooks/useResponsive';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';

/** A piszkos-összevetés pillanatképe — CSAK az elküldendő jelentés (a kikapcsolt nap/óra nem). */
interface LessonRequestSnapshot {
  coachIds: string[];
  date: string | null;
  time: string | null;
  note: string;
}

interface CoachOption {
  userId: string;
  name: string;
}

interface PrivateLessonRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const PrivateLessonRequestDialog: React.FC<PrivateLessonRequestDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { isMobile } = useResponsive();
  const { showSuccess } = useFeedback();

  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [selectedCoaches, setSelectedCoaches] = useState<CoachOption[]>([]);
  const [wantDay, setWantDay] = useState(false);
  const [date, setDate] = useState<Dayjs>(dayjs().add(1, 'day'));
  const [wantTime, setWantTime] = useState(false);
  const [time, setTime] = useState<string>('17:00');
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const confirm = useConfirm();
  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet:
   * `PrivateLessonRequestDialog.tsx:95` (`<Dialog onClose={onClose}>`) — a háttér-kattintás/Esc
   * kérdés nélkül eldobta a kiválasztott edző(ke)t, napot, órát és a megjegyzést.
   * Alapvonal = a megnyitáskori ÜRES igénylés; `false` = zárva → soha nem piszkos.
   */
  const [baselineReady, setBaselineReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setBaselineReady(false);
      return;
    }
    setBaselineReady(true);
    // Reset the form each time the dialog opens.
    setSelectedCoaches([]);
    setWantDay(false);
    setDate(dayjs().add(1, 'day'));
    setWantTime(false);
    setTime('17:00');
    setNote('');
    setError('');

    userService
      .getCoachesBasic()
      .then((list) => setCoaches(list))
      .catch(() => setCoaches([]));
  }, [open]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await eventService.requestPrivateLesson({
        preferredCoachIds: selectedCoaches.length > 0 ? selectedCoaches.map((c) => c.userId) : undefined,
        preferredDate: wantDay ? date.format('YYYY-MM-DD') : null,
        preferredTime: wantTime ? time : null,
        note: note.trim() || undefined,
      });
      setBaselineReady(false);
      onSuccess?.();
      // Siker után NEM egy OK-gombos ablak nyugtázza (projektszabály) — a dialógus bezárul, és a
      // globális toast mondja el az eredményt a Calendar felett. [Web UX #11, 2026-09-12]
      showSuccess('Igénylés elküldve — hamarosan felvesszük veled a kapcsolatot.');
      onClose();
    } catch (err: any) {
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      setError(saveErrorMessage(err, 'Hiba történt a magánóra-igénylés elküldésekor'));
    } finally {
      setLoading(false);
    }
  };

  const EMPTY_REQUEST: LessonRequestSnapshot = { coachIds: [], date: null, time: null, note: '' };
  const isDirty =
    open &&
    isFormDirty<LessonRequestSnapshot>(baselineReady ? EMPTY_REQUEST : null, {
      coachIds: selectedCoaches.map((c) => c.userId),
      // A pipa önmagában (alapértelmezett nappal/órával) is választás: az igénylés tartalmát
      // megváltoztatja, ezért beírt adatnak számít.
      date: wantDay ? date.format('YYYY-MM-DD') : null,
      time: wantTime ? time : null,
      note,
    });
  useUnsavedChanges(isDirty, 'Magánóra-igénylés');

  /** Háttér-kattintás, Esc és „Mégse”: kitöltött igénylésnél a közös ablak kérdez; küldés közben nem zár. */
  const handleClose = async () => {
    if (loading) return;
    if (isDirty && !(await confirm(discardChangesConfirm('magánóra-igénylés')))) return;
    onClose();
  };

  return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Magánóra igénylése</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="body2" color="text.secondary">
              Minden mező opcionális — ha nincs preferenciád, hagyd üresen. Az igényt az egyesület
              e-mailben megkapja.
            </Typography>

            <Autocomplete
              multiple
              options={coaches}
              getOptionLabel={(c) => c.name}
              value={selectedCoaches}
              onChange={(_, v) => setSelectedCoaches(v)}
              isOptionEqualToValue={(o, v) => o.userId === v.userId}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip label={option.name} {...getTagProps({ index })} key={option.userId} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kért edző(k) — opcionális"
                  placeholder="Válassz edzőt (vagy hagyd üresen)"
                />
              )}
            />

            <Box>
              <FormControlLabel
                control={<Checkbox checked={wantDay} onChange={(e) => setWantDay(e.target.checked)} />}
                label="Szeretnék napot megadni"
              />
              {wantDay && (
                <DatePicker
                  label="Kért nap"
                  value={date}
                  onChange={(v) => { if (v) setDate(dayjs(v)); }}
                  format="YYYY-MM-DD"
                  minDate={dayjs()}
                  slotProps={{ textField: { fullWidth: true, sx: { mt: 1 } } }}
                />
              )}
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={wantTime} onChange={(e) => setWantTime(e.target.checked)} />}
                label="Szeretnék órát (időpontot) megadni"
              />
              {wantTime && (
                <TextField
                  label="Kért óra"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <TextField
              label="Megjegyzés (opcionális)"
              fullWidth
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              helperText={`${note.length}/1000`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Mégse</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Igénylés elküldése'}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default PrivateLessonRequestDialog;
